/**
 * Minimal Google Identity Services (GIS) helper.
 *
 * GIS issues an **ID token** (the `credential` field, a JWT) which we POST to
 * `/auth/google`. To obtain it we render the official Google button — GIS does
 * not expose a click-triggered ID-token flow for fully custom buttons.
 *
 * Set `VITE_GOOGLE_CLIENT_ID` to a Google OAuth 2.0 **Web** client ID.
 */
import { env } from '@/config/env'

export interface GoogleCredentialResponse {
  /** The ID token (JWT) to send to the backend as `idToken`. */
  credential: string
  select_by?: string
}

interface GoogleIdConfig {
  client_id: string
  callback: (response: GoogleCredentialResponse) => void
  auto_select?: boolean
  cancel_on_tap_outside?: boolean
}

interface GoogleButtonOptions {
  type?: 'standard' | 'icon'
  theme?: 'outline' | 'filled_blue' | 'filled_black'
  size?: 'large' | 'medium' | 'small'
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
  shape?: 'rectangular' | 'pill' | 'circle' | 'square'
  logo_alignment?: 'left' | 'center'
  width?: number
}

interface GoogleAccountsId {
  initialize: (config: GoogleIdConfig) => void
  renderButton: (parent: HTMLElement, options: GoogleButtonOptions) => void
  prompt: () => void
  cancel: () => void
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } }
  }
}

/** Configured Google client ID (empty string when unset). */
export const GOOGLE_CLIENT_ID = env.googleClientId.trim()

// `hl` forces the button/One Tap UI language; without it GIS follows the
// browser or Google session locale. This is the documented way to set the
// language — the button-level `locale` option is unreliable.
const GIS_SRC = 'https://accounts.google.com/gsi/client?hl=en'

let loader: Promise<void> | null = null

/** Load the GIS client script once and resolve when `window.google` is ready. */
export function loadGoogleIdentity(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('GIS can only load in the browser'))
  }
  if (window.google?.accounts.id) return Promise.resolve()
  if (loader) return loader

  loader = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = GIS_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      loader = null
      reject(new Error('Failed to load Google Sign-In'))
    }
    document.head.appendChild(script)
  })
  return loader
}
